// ============================================================
// ARCHIVO: src/components/TabEvaluacionPractica.jsx
// Rediseñado: panel lateral con los 5 practicarios individuales
// ============================================================
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Stack, TextField, MenuItem,
  Button, Chip, Alert, CircularProgress, Divider,
  Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, TablePagination, IconButton, Tooltip,
  LinearProgress,
} from "@mui/material";
import { FiRefreshCw, FiSearch, FiX, FiExternalLink, FiSave, FiCheckCircle, FiClock } from "react-icons/fi";
import { getAuth } from "firebase/auth";

const API_BASE = "https://vol-backend.onrender.com";

const COLORS = {
  bg:        "#f5f0ff",
  white:     "#ffffff",
  whiteSoft: "#fff8ff",
  subtle:    "#e6dfef",
  red:       "#ff3333",
  redDark:   "#cc0000",
  textMain:  "#2d233a",
  textMuted: "#6c6478",
  green:     "#1b5e20",
  greenBg:   "#e8f5e9",
  orange:    "#e65100",
  orangeBg:  "#fff3e0",
  danger:    "#b00020",
  dangerBg:  "#ffebee",
  purple:    "#4a148c",
  purpleBg:  "#f3e5f5",
};

// ── Los 5 practicarios con etiquetas y colores ──────────────
const PRACTICARIOS = [
  { tipo: "BLS",                 label: "BLS",                    short: "BLS",    color: "#1565c0", bg: "#e3f2fd" },
  { tipo: "evaluacion_paciente", label: "Evaluación del Paciente", short: "Ev. Pac", color: "#6a1b9a", bg: "#f3e5f5" },
  { tipo: "abdomen",             label: "Abdomen",                 short: "Abd",    color: "#2e7d32", bg: "#e8f5e9" },
  { tipo: "torax",               label: "Tórax",                   short: "Tórax",  color: "#e65100", bg: "#fff3e0" },
  { tipo: "supraglotico",        label: "Supraglótico",            short: "Supr.",  color: "#00695c", bg: "#e0f2f1" },
];

async function authedFetch(url, opts = {}) {
  const user = getAuth().currentUser;
  if (!user) throw new Error("No autenticado");
  const token = await user.getIdToken();
  const res = await fetch(url, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      Authorization: `Bearer ${token}`,
      "x-firebase-uid": user.uid,
    },
  });
  if (!res.ok) { const t = await res.text(); throw new Error(t || `HTTP ${res.status}`); }
  return res.json();
}

function safeStr(v) { return v === null || v === undefined ? "" : String(v); }
function fullName(r) { return `${safeStr(r?.nombre)} ${safeStr(r?.apellido_pat)}`.trim(); }

// ── Badge de calificación ───────────────────────────────────
function ScoreBadge({ score, size = "normal" }) {
  if (score === null || score === undefined)
    return <Typography sx={{ color: "#aaa", fontSize: size === "small" ? 11 : 12 }}>Sin calificar</Typography>;
  const n = Number(score);
  const color = n >= 8 ? COLORS.green : n >= 6 ? COLORS.orange : COLORS.danger;
  const bg    = n >= 8 ? COLORS.greenBg : n >= 6 ? COLORS.orangeBg : COLORS.dangerBg;
  return (
    <Box sx={{
      display: "inline-block",
      px: size === "small" ? 0.8 : 1,
      py: size === "small" ? 0.2 : 0.3,
      borderRadius: 1,
      backgroundColor: bg,
      color,
      fontWeight: 800,
      fontSize: size === "small" ? 11 : 13,
    }}>
      {n.toFixed(1)}
    </Box>
  );
}

// ── Mini chips de los 5 practicarios para la tabla ─────────
function PracticariosMini({ practicarios }) {
  if (!Array.isArray(practicarios) || practicarios.length === 0) {
    return <Typography sx={{ color: "#aaa", fontSize: 11 }}>Sin datos</Typography>;
  }
  return (
    <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap>
      {PRACTICARIOS.map(p => {
        const found = practicarios.find(x => x.tipo === p.tipo);
        const score = found?.score;
        const hasScore = score !== null && score !== undefined;
        const n = hasScore ? Number(score) : null;
        const color = !hasScore ? "#aaa" : n >= 8 ? COLORS.green : n >= 6 ? COLORS.orange : COLORS.danger;
        const bg = !hasScore ? "#f5f5f5" : n >= 8 ? COLORS.greenBg : n >= 6 ? COLORS.orangeBg : COLORS.dangerBg;
        return (
          <Tooltip key={p.tipo} title={`${p.label}: ${hasScore ? n.toFixed(1) : "pendiente"}`}>
            <Box sx={{
              px: 0.7, py: 0.1, borderRadius: 1,
              backgroundColor: bg, color,
              fontWeight: 700, fontSize: 10,
              border: `1px solid ${color}33`,
              cursor: "default",
            }}>
              {p.short}
              {hasScore && ` ${n.toFixed(1)}`}
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );
}

// ── Panel lateral con los 5 practicarios ───────────────────
function PanelPracticarios({ selected, onClose, onGuardado }) {
  const [loadingPracticarios, setLoadingPracticarios] = useState(false);
  const [errorPracticarios, setErrorPracticarios]     = useState("");
  const [practicarios, setPracticarios]               = useState([]);
  const [promedio, setPromedio]                       = useState(null);
  const [totalCalificados, setTotalCalificados]       = useState(0);

  // Estado de edición por tipo: { [tipo]: { score: "", pdf_url: "" } }
  const [edits, setEdits]   = useState({});
  const [saving, setSaving] = useState({});  // { [tipo]: bool }
  const [msgs, setMsgs]     = useState({});  // { [tipo]: string }

  const fetchPracticarios = useCallback(async () => {
    if (!selected?.user_id || !selected?.block_id) return;
    setLoadingPracticarios(true);
    setErrorPracticarios("");
    try {
      const data = await authedFetch(
        `${API_BASE}/progreso/admin/practicarios/${selected.user_id}/${selected.block_id}`
      );
      setPracticarios(Array.isArray(data.practicarios) ? data.practicarios : []);
      setPromedio(data.promedio ?? null);
      setTotalCalificados(data.total_calificados ?? 0);

      // Pre-llenar edits con valores actuales para facilitar corrección
      const init = {};
      (data.practicarios || []).forEach(p => {
        init[p.tipo] = {
          score:   p.score !== null && p.score !== undefined ? String(p.score) : "",
          pdf_url: p.pdf_url || "",
        };
      });
      setEdits(init);
      setMsgs({});
    } catch (e) {
      setErrorPracticarios(e.message);
    } finally {
      setLoadingPracticarios(false);
    }
  }, [selected?.user_id, selected?.block_id]);

  useEffect(() => {
    fetchPracticarios();
  }, [fetchPracticarios]);

  const handleEdit = (tipo, field, value) => {
    setEdits(prev => ({
      ...prev,
      [tipo]: { ...(prev[tipo] || { score: "", pdf_url: "" }), [field]: value },
    }));
  };

  const handleSave = async (tipo) => {
    const edit = edits[tipo] || {};
    if (edit.score === "" || edit.score === null || edit.score === undefined) {
      setMsgs(prev => ({ ...prev, [tipo]: "⚠️ Ingresa una calificación" }));
      return;
    }
    const scoreNum = Number(edit.score);
    if (isNaN(scoreNum) || scoreNum < 0 || scoreNum > 10) {
      setMsgs(prev => ({ ...prev, [tipo]: "⚠️ La calificación debe ser entre 0 y 10" }));
      return;
    }

    setSaving(prev => ({ ...prev, [tipo]: true }));
    setMsgs(prev => ({ ...prev, [tipo]: "" }));

    try {
      const result = await authedFetch(
        `${API_BASE}/progreso/admin/practicarios/${selected.user_id}/${selected.block_id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            tipo,
            score: scoreNum,
            pdf_url: edit.pdf_url || null,
            programa: selected.bloque_nombre || "PNA",
          }),
        }
      );

      setMsgs(prev => ({ ...prev, [tipo]: `✅ Guardado${result.bloque_recalculado ? " · Bloque recalculado" : ""}` }));
      await fetchPracticarios();
      if (onGuardado) onGuardado();
    } catch (e) {
      setMsgs(prev => ({ ...prev, [tipo]: `❌ ${e.message}` }));
    } finally {
      setSaving(prev => ({ ...prev, [tipo]: false }));
    }
  };

  const progresoPct = Math.round((totalCalificados / 5) * 100);

  return (
    <Paper elevation={2} sx={{
      width: 360,
      flexShrink: 0,
      borderRadius: 3,
      p: 0,
      border: `1px solid ${COLORS.subtle}`,
      backgroundColor: COLORS.white,
      alignSelf: "flex-start",
      position: "sticky",
      top: 16,
      overflow: "hidden",
    }}>
      {/* ── Encabezado ── */}
      <Box sx={{
        px: 2, py: 1.5,
        background: `linear-gradient(135deg, ${COLORS.whiteSoft} 0%, #ede7f6 100%)`,
        borderBottom: `1px solid ${COLORS.subtle}`,
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flex: 1, mr: 1 }}>
            <Typography sx={{ fontWeight: 900, fontSize: 14, color: COLORS.textMain }}>
              Practicarios
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 13, color: COLORS.purple, mt: 0.2 }}>
              {fullName(selected)}
            </Typography>
            <Typography sx={{ color: COLORS.textMuted, fontSize: 11 }}>
              {safeStr(selected.matricula)} · {safeStr(selected.bloque_nombre)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ mt: -0.5 }}>
            <FiX size={14} />
          </IconButton>
        </Stack>

        {/* Barra de progreso */}
        <Box sx={{ mt: 1.2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.4 }}>
            <Typography sx={{ fontSize: 10, color: COLORS.textMuted }}>
              {totalCalificados}/5 practicarios calificados
            </Typography>
            {promedio !== null && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={{ fontSize: 10, color: COLORS.textMuted }}>Promedio:</Typography>
                <ScoreBadge score={promedio} size="small" />
              </Box>
            )}
          </Stack>
          <LinearProgress
            variant="determinate"
            value={progresoPct}
            sx={{
              height: 5,
              borderRadius: 3,
              backgroundColor: COLORS.subtle,
              "& .MuiLinearProgress-bar": {
                backgroundColor: progresoPct === 100 ? COLORS.green : COLORS.purple,
                borderRadius: 3,
              },
            }}
          />
        </Box>
      </Box>

      {/* ── Contenido ── */}
      <Box sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}>
        {loadingPracticarios ? (
          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={16} />
            <Typography sx={{ color: COLORS.textMuted, fontSize: 13 }}>Cargando practicarios…</Typography>
          </Stack>
        ) : errorPracticarios ? (
          <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>{errorPracticarios}</Alert>
        ) : (
          <Stack spacing={0} divider={<Divider sx={{ borderColor: COLORS.subtle + "80" }} />}>
            {PRACTICARIOS.map((practica) => {
              const data    = practicarios.find(p => p.tipo === practica.tipo) || {};
              const hasScore = data.score !== null && data.score !== undefined;
              const edit    = edits[practica.tipo] || { score: "", pdf_url: "" };
              const isSaving = !!saving[practica.tipo];
              const msg     = msgs[practica.tipo] || "";

              return (
                <Box key={practica.tipo} sx={{ px: 2, py: 1.5 }}>
                  {/* Título del practicario */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <Box sx={{
                        width: 8, height: 8, borderRadius: "50%",
                        backgroundColor: hasScore ? COLORS.green : "#ccc",
                        flexShrink: 0,
                      }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.textMain }}>
                        {practica.label}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {hasScore ? (
                        <>
                          <FiCheckCircle size={12} color={COLORS.green} />
                          <ScoreBadge score={data.score} size="small" />
                        </>
                      ) : (
                        <>
                          <FiClock size={11} color="#aaa" />
                          <Typography sx={{ fontSize: 11, color: "#aaa" }}>Pendiente</Typography>
                        </>
                      )}
                    </Stack>
                  </Stack>

                  {/* PDF existente */}
                  {data.pdf_url && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.8 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FiExternalLink size={11} />}
                        onClick={() => window.open(data.pdf_url, "_blank", "noopener,noreferrer")}
                        sx={{
                          fontSize: 10,
                          py: 0.2,
                          px: 0.8,
                          borderRadius: 1.5,
                          borderColor: practica.color + "80",
                          color: practica.color,
                          textTransform: "none",
                          minWidth: 0,
                          "&:hover": { backgroundColor: practica.bg },
                        }}
                      >
                        Ver PDF
                      </Button>
                      {data.subido_por && data.subido_por !== "poller" && (
                        <Typography sx={{ fontSize: 10, color: COLORS.textMuted }}>
                          por {data.subido_por}
                        </Typography>
                      )}
                    </Stack>
                  )}

                  {/* Formulario de edición manual */}
                  <Stack spacing={0.6}>
                    <Stack direction="row" spacing={0.6} alignItems="center">
                      <TextField
                        size="small"
                        type="number"
                        label="Calificación (0-10)"
                        value={edit.score}
                        onChange={e => handleEdit(practica.tipo, "score", e.target.value)}
                        inputProps={{ min: 0, max: 10, step: 0.1 }}
                        sx={{
                          flex: 1,
                          backgroundColor: COLORS.white,
                          "& .MuiOutlinedInput-root": {
                            fontSize: 12,
                            "& fieldset": { borderColor: practica.color + "60" },
                            "&:hover fieldset": { borderColor: practica.color },
                          },
                          "& .MuiInputLabel-root": { fontSize: 11 },
                        }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        disabled={isSaving}
                        onClick={() => handleSave(practica.tipo)}
                        sx={{
                          backgroundColor: practica.color,
                          "&:hover": { backgroundColor: practica.color + "dd", filter: "brightness(0.9)" },
                          borderRadius: 1.5,
                          fontWeight: 900,
                          textTransform: "none",
                          fontSize: 11,
                          minWidth: 0,
                          px: 1.2,
                          py: 0.6,
                        }}
                      >
                        {isSaving ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : <FiSave size={13} />}
                      </Button>
                    </Stack>

                    {/* Campo URL PDF (opcional) */}
                    <TextField
                      size="small"
                      label="URL del PDF (opcional)"
                      value={edit.pdf_url}
                      onChange={e => handleEdit(practica.tipo, "pdf_url", e.target.value)}
                      placeholder="https://drive.google.com/..."
                      sx={{
                        backgroundColor: COLORS.white,
                        "& .MuiOutlinedInput-root": { fontSize: 11 },
                        "& .MuiInputLabel-root": { fontSize: 11 },
                      }}
                    />
                  </Stack>

                  {/* Mensaje de estado */}
                  {msg && (
                    <Alert
                      severity={msg.startsWith("✅") ? "success" : msg.startsWith("⚠️") ? "warning" : "error"}
                      sx={{ mt: 0.8, py: 0, borderRadius: 1.5, fontSize: 11 }}
                    >
                      {msg}
                    </Alert>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* ── Footer: promedio ── */}
      {promedio !== null && (
        <Box sx={{
          px: 2, py: 1.2,
          borderTop: `1px solid ${COLORS.subtle}`,
          backgroundColor: COLORS.greenBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <FiCheckCircle size={14} color={COLORS.green} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.green }}>
              Promedio final de practicarios
            </Typography>
          </Stack>
          <ScoreBadge score={promedio} />
        </Box>
      )}

      <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${COLORS.subtle}` }}>
        <Typography sx={{ fontSize: 10, color: COLORS.textMuted, textAlign: "center" }}>
          Al guardar se recalcula el bloque automáticamente.
        </Typography>
      </Box>
    </Paper>
  );
}

// ── Componente principal ────────────────────────────────────
export default function TabEvaluacionPractica() {
  const [programa, setPrograma]   = useState("");
  const [estado, setEstado]       = useState("");
  const [matricula, setMatricula] = useState("");
  const [bloque, setBloque]       = useState("");
  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const [programas, setProgramas]             = useState([]);
  const [estadosScope, setEstadosScope]       = useState([]);
  const [groupCodesScope, setGroupCodesScope] = useState([]);
  const [esAdmin, setEsAdmin]                 = useState(false);
  const [bloquesDisponibles, setBloquesDisponibles] = useState([]);

  const [selected, setSelected] = useState(null);

  useEffect(() => {
    authedFetch(`${API_BASE}/progreso/admin/evaluar/programas`)
      .then(d => {
        setProgramas(Array.isArray(d?.programs) ? d.programs : []);
        setEstadosScope(Array.isArray(d?.estados) ? d.estados : []);
        setGroupCodesScope(Array.isArray(d?.groupCodes) ? d.groupCodes : []);
        setEsAdmin(!d?.estados || d.estados === null);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!programa) { setBloquesDisponibles([]); setBloque(""); return; }
    authedFetch(`${API_BASE}/progreso/admin/evaluar/practica?programa=${programa}&limit=200`)
      .then(d => {
        const rows = d?.practica || [];
        const seen = new Set();
        const unique = [];
        rows.forEach(r => {
          if (!seen.has(r.block_id)) {
            seen.add(r.block_id);
            unique.push({ block_id: r.block_id, nombre: safeStr(r.bloque_nombre) });
          }
        });
        setBloquesDisponibles(unique);
      })
      .catch(() => setBloquesDisponibles([]));
  }, [programa]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url = new URL(`${API_BASE}/progreso/admin/evaluar/practica`);
      url.searchParams.set("page", page + 1);
      url.searchParams.set("limit", rowsPerPage);
      if (programa) url.searchParams.set("programa", programa);
      if (estado) {
        if (groupCodesScope.length > 0) url.searchParams.set("group_code", estado);
        else url.searchParams.set("estado", estado);
      }
      if (bloque) url.searchParams.set("bloque", bloque);
      if (matricula.trim()) url.searchParams.set("matricula", matricula.trim());
      const res = await authedFetch(url.toString());
      setData(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [programa, estado, matricula, bloque, page, rowsPerPage, groupCodesScope]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const practica = data?.practica || [];
  const stats    = data?.stats    || {};
  const total    = data?.pagination?.total || 0;

  const estadoOpciones = esAdmin
    ? ["Aguascalientes","Baja California","Baja California Sur","Campeche","Chiapas","Chihuahua",
       "Ciudad de México","Coahuila","Colima","Durango","Estado de México","Guanajuato","Guerrero",
       "Hidalgo","Jalisco","Michoacán","Morelos","Nayarit","Nuevo León","Oaxaca","Puebla",
       "Querétaro","Quintana Roo","San Luis Potosí","Sinaloa","Sonora","Tabasco","Tamaulipas",
       "Tlaxcala","Veracruz","Yucatán","Zacatecas"]
    : groupCodesScope.length > 0 ? groupCodesScope : estadosScope;

  return (
    <Box sx={{ mt: 2, display: "flex", gap: 2, alignItems: "flex-start" }}>
      {/* ── PANEL PRINCIPAL ── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Paper elevation={0} sx={{
          p: 2, borderRadius: 3,
          backgroundColor: COLORS.white,
          border: `1px solid ${COLORS.subtle}`,
          mb: 2,
        }}>

          {/* Stats */}
          <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
            {[
              { label: "Total",         value: stats.total         ?? "—", color: COLORS.textMain },
              { label: "Aprobados",     value: stats.aprobados     ?? "—", color: COLORS.green },
              { label: "Sin calificar", value: stats.sin_calificar ?? "—", color: COLORS.orange },
              { label: "Reprobados",    value: stats.reprobados    ?? "—", color: COLORS.danger },
            ].map(s => (
              <Paper key={s.label} elevation={0} sx={{
                px: 2, py: 1.2, borderRadius: 2,
                border: `1px solid ${COLORS.subtle}`,
                minWidth: 120,
              }}>
                <Typography sx={{ fontSize: 22, fontWeight: 900, color: s.color, lineHeight: 1 }}>
                  {s.value}
                </Typography>
                <Typography sx={{ fontSize: 11, color: COLORS.textMuted, mt: 0.3 }}>
                  {s.label}
                </Typography>
              </Paper>
            ))}
          </Stack>

          {/* Filtros */}
          <Stack direction="row" flexWrap="wrap" useFlexGap gap={1.2} alignItems="center" sx={{ mb: 1.5 }}>
            <TextField select size="small" label="Programa" value={programa}
              onChange={e => { setPrograma(e.target.value); setBloque(""); setPage(0); }}
              sx={{ width: 260, backgroundColor: COLORS.white }}>
              <MenuItem value="">Todos los programas</MenuItem>
              {programas.map(p => (
                <MenuItem key={p.code} value={p.code}>{p.name} ({p.code})</MenuItem>
              ))}
            </TextField>

            <TextField select size="small" label="Bloque" value={bloque}
              onChange={e => { setBloque(e.target.value); setPage(0); }}
              sx={{ width: 220, backgroundColor: COLORS.white }}
              disabled={!programa}>
              <MenuItem value="">Todos los bloques</MenuItem>
              {bloquesDisponibles.map(b => (
                <MenuItem key={b.block_id} value={b.block_id}>{b.nombre}</MenuItem>
              ))}
            </TextField>

            <TextField select size="small"
              label={groupCodesScope.length > 0 ? "Grupo AlEn" : "Estado"}
              value={estado}
              onChange={e => { setEstado(e.target.value); setPage(0); }}
              sx={{ width: 200, backgroundColor: COLORS.white }}>
              <MenuItem value="">
                {groupCodesScope.length > 0 ? "Todos mis grupos" : "Todos mis estados"}
              </MenuItem>
              {estadoOpciones.map(e => (
                <MenuItem key={e} value={e}>{e}</MenuItem>
              ))}
            </TextField>

            <TextField size="small" label="Matrícula" value={matricula}
              onChange={e => { setMatricula(e.target.value); setPage(0); }}
              sx={{ width: 160, backgroundColor: COLORS.white }}
              InputProps={{
                endAdornment: (
                  <Tooltip title="Buscar">
                    <IconButton onClick={fetchData}><FiSearch /></IconButton>
                  </Tooltip>
                ),
              }}
            />

            <Tooltip title="Refrescar">
              <IconButton onClick={fetchData}
                sx={{ border: `1px solid ${COLORS.subtle}`, borderRadius: 2, backgroundColor: COLORS.white }}>
                <FiRefreshCw />
              </IconButton>
            </Tooltip>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>{error}</Alert>}
          <Divider sx={{ mb: 1.5, borderColor: COLORS.subtle }} />

          {/* Tabla */}
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    "Aspirante",
                    "Estado",
                    "Bloque",
                    "Estado bloque",
                    "Practicarios (5)",
                    "Práctica final",
                    "Nota final",
                    "",
                  ].map(h => (
                    <TableCell key={h} sx={{ backgroundColor: COLORS.whiteSoft, fontWeight: 900, fontSize: 12 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 4, textAlign: "center" }}>
                      <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                        <CircularProgress size={18} />
                        <Typography sx={{ color: COLORS.textMuted }}>Cargando…</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : practica.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 4, textAlign: "center" }}>
                      <Typography sx={{ color: COLORS.textMuted }}>Sin registros.</Typography>
                    </TableCell>
                  </TableRow>
                ) : practica.map((row, idx) => {
                  const isSelected = selected?.user_id === row.user_id && selected?.block_id === row.block_id;
                  return (
                    <TableRow
                      key={idx}
                      hover
                      selected={isSelected}
                      sx={isSelected ? { backgroundColor: COLORS.purpleBg + "60" } : {}}
                    >
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                          {fullName(row)}
                        </Typography>
                        <Typography sx={{ color: COLORS.textMuted, fontSize: 11 }}>
                          {safeStr(row.matricula) || <em style={{ color: "#f44336" }}>Sin matrícula</em>}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={safeStr(row.estado_usuario) || "—"}
                          size="small"
                          sx={{ fontSize: 10, height: 18 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ fontSize: 12 }}>{safeStr(row.bloque_nombre)}</Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={
                            row.bloque_status === "aprobado" ? "Aprobado" :
                            row.bloque_status === "reprobado" ? "Reprobado" : "En progreso"
                          }
                          size="small"
                          sx={{
                            fontSize: 10, height: 20, fontWeight: 700,
                            backgroundColor:
                              row.bloque_status === "aprobado" ? COLORS.greenBg :
                              row.bloque_status === "reprobado" ? COLORS.dangerBg : COLORS.orangeBg,
                            color:
                              row.bloque_status === "aprobado" ? COLORS.green :
                              row.bloque_status === "reprobado" ? COLORS.danger : COLORS.orange,
                          }}
                        />
                      </TableCell>

                      {/* Mini chips de los 5 practicarios */}
                      <TableCell sx={{ minWidth: 200 }}>
                        <PracticariosMini practicarios={row.practicarios_detalle || []} />
                      </TableCell>

                      <TableCell>
                        <ScoreBadge score={row.practical_score} size="small" />
                      </TableCell>

                      <TableCell>
                        <ScoreBadge score={row.nota_final_bloque} size="small" />
                      </TableCell>

                      <TableCell>
                        <Button
                          size="small"
                          variant={isSelected ? "outlined" : "contained"}
                          onClick={() => setSelected(isSelected ? null : row)}
                          sx={isSelected ? {
                            borderColor: COLORS.purple,
                            color: COLORS.purple,
                            borderRadius: 2,
                            fontWeight: 900,
                            textTransform: "none",
                            fontSize: 11,
                          } : {
                            backgroundColor: COLORS.red,
                            "&:hover": { backgroundColor: COLORS.redDark },
                            borderRadius: 2,
                            fontWeight: 900,
                            textTransform: "none",
                            fontSize: 11,
                          }}
                        >
                          {isSelected ? "Cerrar" : "Ver / Calificar"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="Por página:"
          />
        </Paper>
      </Box>

      {/* ── PANEL LATERAL DE PRACTICARIOS ── */}
      {selected && (
        <PanelPracticarios
          selected={selected}
          onClose={() => setSelected(null)}
          onGuardado={fetchData}
        />
      )}
    </Box>
  );
}